/**
 * Message Processor
 * 
 * Phase 2: Message Processing
 * 
 * Optimizes message processing by tracking processed messages,
 * preventing duplicate processing, and managing processing state.
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Message processing status
 */
export const ProcessingStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  PROCESSED: 'PROCESSED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED'
};

/**
 * Message Processor
 * 
 * Tracks processed messages and prevents duplicate processing
 */
export class MessageProcessor {
  constructor(options = {}) {
    this.maxCacheSize = options.maxCacheSize || 1000;
    this.cacheExpiry = options.cacheExpiry || 3600000; // 1 hour
    this.enableDeduplication = options.enableDeduplication !== false;
    
    // Track processed message hashes
    // hash -> { timestamp, status, messageId }
    this.processedMessages = new Map();
    
    // Track processing statistics
    this.stats = {
      totalReceived: 0,
      totalProcessed: 0,
      totalDuplicates: 0,
      totalFailed: 0,
      totalSkipped: 0,
      processingTimes: [],
      averageProcessingTime: 0
    };
    
    // Start cleanup interval
    this.startCleanupInterval();
  }
  
  /**
   * Check if message has already been processed
   * 
   * @param {string} hash - Message hash
   * @returns {boolean} True if already processed
   */
  isProcessed(hash) {
    if (!this.enableDeduplication) {
      return false;
    }
    
    if (!hash) {
      return false;
    }
    
    const record = this.processedMessages.get(hash);
    if (!record) {
      return false;
    }
    
    // Check if record has expired
    const age = Date.now() - record.timestamp;
    if (age > this.cacheExpiry) {
      this.processedMessages.delete(hash);
      return false;
    }
    
    return record.status === ProcessingStatus.PROCESSED;
  }
  
  /**
   * Mark message as being processed
   * 
   * @param {string} hash - Message hash
   * @param {string|number} messageId - Message ID
   */
  markProcessing(hash, messageId) {
    if (!hash) return;
    
    this.processedMessages.set(hash, {
      timestamp: Date.now(),
      status: ProcessingStatus.PROCESSING,
      messageId: messageId
    });
  }
  
  /**
   * Mark message as processed
   * 
   * @param {string} hash - Message hash
   * @param {number} processingTime - Time taken to process (ms)
   */
  markProcessed(hash, processingTime = 0) {
    if (!hash) return;
    
    const record = this.processedMessages.get(hash);
    if (record) {
      record.status = ProcessingStatus.PROCESSED;
      record.processingTime = processingTime;
    } else {
      this.processedMessages.set(hash, {
        timestamp: Date.now(),
        status: ProcessingStatus.PROCESSED,
        processingTime: processingTime
      });
    }
    
    this.stats.totalProcessed++;
    
    // Update average processing time
    if (processingTime > 0) {
      this.stats.processingTimes.push(processingTime);
      if (this.stats.processingTimes.length > 100) {
        this.stats.processingTimes.shift();
      }
      this.stats.averageProcessingTime =
        this.stats.processingTimes.reduce((a, b) => a + b, 0) / this.stats.processingTimes.length;
    }
    
    // Cleanup if cache is too large
    if (this.processedMessages.size > this.maxCacheSize) {
      this.cleanup();
    }
  }
  
  /**
   * Mark message as failed
   * 
   * @param {string} hash - Message hash
   * @param {Error} error - Error that occurred
   */
  markFailed(hash, error) {
    if (!hash) return;
    
    const record = this.processedMessages.get(hash);
    if (record) {
      record.status = ProcessingStatus.FAILED;
      record.error = error.message;
    } else {
      this.processedMessages.set(hash, {
        timestamp: Date.now(),
        status: ProcessingStatus.FAILED,
        error: error.message
      });
    }
    
    this.stats.totalFailed++;
  }
  
  /**
   * Mark message as duplicate (skipped)
   * 
   * @param {string} hash - Message hash
   */
  markDuplicate(hash) {
    if (!hash) return;
    
    this.stats.totalDuplicates++;
    
    // Update timestamp of existing record
    const record = this.processedMessages.get(hash);
    if (record) {
      record.lastSeen = Date.now();
      record.duplicateCount = (record.duplicateCount || 0) + 1;
    }
  }
  
  /**
   * Record received message
   */
  recordReceived() {
    this.stats.totalReceived++;
  }
  
  /**
   * Process a message with deduplication
   * 
   * @param {Object} message - Message to process
   * @param {Function} processor - Processing function
   * @returns {Promise} Processing result
   */
  async processMessage(message, processor) {
    this.recordReceived();
    
    const hash = message.hash;
    const messageId = message.id;
    
    // Check for duplicates
    if (this.isProcessed(hash)) {
      console.log(`⏭️ Skipping duplicate message: ${messageId || hash}`);
      this.markDuplicate(hash);
      return {
        status: ProcessingStatus.SKIPPED,
        reason: 'duplicate',
        hash: hash
      };
    }
    
    // Mark as processing
    this.markProcessing(hash, messageId);
    
    const startTime = Date.now();
    
    try {
      // Process the message
      const result = await processor(message);
      
      const processingTime = Date.now() - startTime;
      this.markProcessed(hash, processingTime);
      
      console.log(`✅ Processed message ${messageId || hash} in ${processingTime}ms`);
      
      return {
        status: ProcessingStatus.PROCESSED,
        result: result,
        processingTime: processingTime,
        hash: hash
      };
    } catch (error) {
      console.error(`❌ Failed to process message ${messageId || hash}:`, error);
      this.markFailed(hash, error);
      
      return {
        status: ProcessingStatus.FAILED,
        error: error,
        hash: hash
      };
    }
  }
  
  /**
   * Cleanup expired records
   */
  cleanup() {
    const now = Date.now();
    let removed = 0;
    
    for (const [hash, record] of this.processedMessages.entries()) {
      const age = now - record.timestamp;
      if (age > this.cacheExpiry) {
        this.processedMessages.delete(hash);
        removed++;
      }
    }
    
    if (removed > 0) {
      console.log(`🗑️ Cleaned up ${removed} expired message records`);
    }
    
    // If still too large, remove oldest entries
    if (this.processedMessages.size > this.maxCacheSize) {
      const entries = Array.from(this.processedMessages.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = this.processedMessages.size - this.maxCacheSize;
      for (let i = 0; i < toRemove; i++) {
        this.processedMessages.delete(entries[i][0]);
      }
      
      console.log(`🗑️ Removed ${toRemove} oldest message records to maintain cache size`);
    }
  }
  
  /**
   * Start automatic cleanup interval
   */
  startCleanupInterval() {
    // Cleanup every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 300000);
  }
  
  /**
   * Stop automatic cleanup interval
   */
  stopCleanupInterval() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
  
  /**
   * Get processing statistics
   * 
   * @returns {Object} Statistics object
   */
  getStats() {
    return {
      totalReceived: this.stats.totalReceived,
      totalProcessed: this.stats.totalProcessed,
      totalDuplicates: this.stats.totalDuplicates,
      totalFailed: this.stats.totalFailed,
      totalSkipped: this.stats.totalSkipped,
      cacheSize: this.processedMessages.size,
      maxCacheSize: this.maxCacheSize,
      deduplicationRate: this.stats.totalReceived > 0
        ? ((this.stats.totalDuplicates / this.stats.totalReceived) * 100).toFixed(2) + '%'
        : '0%',
      successRate: this.stats.totalReceived > 0
        ? ((this.stats.totalProcessed / this.stats.totalReceived) * 100).toFixed(2) + '%'
        : '0%',
      averageProcessingTime: Math.round(this.stats.averageProcessingTime) + 'ms'
    };
  }
  
  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalReceived: 0,
      totalProcessed: 0,
      totalDuplicates: 0,
      totalFailed: 0,
      totalSkipped: 0,
      processingTimes: [],
      averageProcessingTime: 0
    };
    console.log('📊 Reset message processing statistics');
  }
  
  /**
   * Clear all processed messages
   */
  clear() {
    this.processedMessages.clear();
    console.log('🗑️ Cleared all processed message records');
  }
  
  /**
   * Destroy processor and cleanup
   */
  destroy() {
    this.stopCleanupInterval();
    this.clear();
    console.log('💥 Message processor destroyed');
  }
}

